import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { RRule, Weekday } from 'rrule';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, NavigationHeader, Spacer, TextButton } from 'design-system';
import BottomPanel from 'design-system/BottomPanel';
import { HEIGHT } from 'design-system/Button';
import CardRow from 'design-system/CardRow';
import CardRowContainer from 'design-system/CardRowContainer';
import { IconCoins } from 'design-system/icons/IconCoins';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconTooltip from 'design-system/icons/IconTooltip';
import { LineSeparator } from 'design-system/LineSeparator';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import { hitSlopS, rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useOnboardingState } from 'features/invest/hooks/selectors';
import { formatDateInProgressTimeline } from 'features/invest/utils';
import AutosaveFeeInfoModal from 'features/pots/components/AutosaveFeeInfoModal';
import { selectAutosaveFees } from 'features/pots/reducers/selectors';
import { getPeriodFromRRule } from 'features/pots/utils';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { formatAmount, getOrdinal } from 'utils';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import Links, { knownLinks } from 'utils/links';
import Modal from 'utils/packages/Modal';
import { OnboardingFlow } from 'utils/types/navigation';
import { getPotsEstimatedCollectionDate } from '../actions';
import useHandleCreatePotsAccount from '../hooks/useHandleCreatePotsAccount';
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const RowLabelWithTooltip = ({ label, onPress }) => {
    const styles = useStyles(styleSet);
    return (<View style={styles.row}>
      <Text TextThin-14 Secondary>
        {label}
      </Text>
      <Spacer.W8 />
      <View>
        <Spacer h={4}/>
        <TouchableOpacity hitSlop={hitSlopS} onPress={onPress}>
          <IconTooltip height={14} width={14} colorKey="secondary"/>
        </TouchableOpacity>
      </View>
    </View>);
};
const PotsOnboardingReviewDepositAndAutosaveInfoScreen = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    const onboarding = useAppSelector((state) => state.pots.onboarding);
    const fundingAccount = useOnboardingState()?.fundingAccount;
    const fees = useAppSelector(selectAutosaveFees);
    const { createPotAccount } = useHandleCreatePotsAccount();
    const scrollY = useSharedValue(0);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const accountName = fundingAccount && 'name' in fundingAccount && fundingAccount.name && fundingAccount.bankName
        ? `${fundingAccount.bankName} · ${fundingAccount.name}`
        : fundingAccount?.bankName || 'Linked bank';
    const initialDepositAmount = onboarding?.initialDepositAmount;
    const [potsEstimatedCollectionDate, , fetchingEstimatedDate, fetchPotsEstimatedCollectionDate] = useFetchOld();
    useEffect(() => {
        if (initialDepositAmount !== undefined) {
            fetchPotsEstimatedCollectionDate(getPotsEstimatedCollectionDate());
        }
    }, [initialDepositAmount, fetchPotsEstimatedCollectionDate]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('Review Direct Debit Screen', { source: 'save' });
        }
    }, [mountedOnAnotherScreen]);
    const estimatedCollectionDate = useMemo(() => {
        if (!potsEstimatedCollectionDate?.estimatedCollectionDate)
            return '';
        return formatDateInProgressTimeline(potsEstimatedCollectionDate.estimatedCollectionDate);
    }, [potsEstimatedCollectionDate]);
    const collectionDateString = useMemo(() => {
        const rrule = onboarding?.collectionFrequency?.rrule;
        if (!rrule)
            return '';
        const parsed = RRule.parseString(rrule);
        if (getPeriodFromRRule(rrule) !== 'Monthly') {
            const weekday = Array.isArray(parsed.byweekday) ? parsed.byweekday[0] : parsed.byweekday;
            return `Every ${weekday instanceof Weekday ? WEEKDAY_NAMES[weekday.weekday] : 'week'}`;
        }
        if (parsed.bymonthday === 1) {
            return 'First working day of each month';
        }
        if (parsed.bymonthday === -1) {
            return 'Last working day of each month';
        }
        if (typeof parsed.bymonthday === 'number') {
            return `${getOrdinal(parsed.bymonthday)} of each month`;
        }
        return 'Every month';
    }, [onboarding?.collectionFrequency?.rrule]);
    const savingRules = useMemo(() => {
        const parts = [];
        if (onboarding?.automated)
            parts.push('AI');
        if (onboarding?.contributionAmount)
            parts.push('Custom');
        if (onboarding?.roundUpMultiplier)
            parts.push('Round Ups');
        return parts.join(', ');
    }, [onboarding?.automated, onboarding?.contributionAmount, onboarding?.roundUpMultiplier]);
    const handleLearnMore = useCallback(() => {
        if (Platform.OS === 'android') {
            navigation.navigateDeprecated('Pdf', { url: knownLinks.potsTnc, buttonTitle: 'Done' });
        }
        else {
            Links.openPotsTnc();
        }
    }, []);
    const showFeeModal = useCallback(() => {
        if (typeof fees === 'number') {
            AutosaveFeeInfoModal.show({ value: fees, currency: 'GBP' });
        }
    }, [fees]);
    const showEstimatedCollectionDateModal = useCallback(() => {
        Modal.showConfirmation({
            title: 'Estimated collection date',
            subtitle: 'We’re setting up a Direct Debit to collect your initial deposit. This can take 3–5 working days to complete. Once it does, we’ll add the money directly to your pot.',
            confirmButtonTitle: 'Okay',
        });
    }, []);
    const handleConfirm = useCallback(() => {
        createPotAccount();
    }, [createPotAccount]);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    return (<AppView type="secondary" withFrame={false}>
        <NavigationHeader title="" scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} rightContent={<View style={mountedOnAnotherScreen ? styles.headerRight : null}>
              <HelpScreenButton />
            </View>}/>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Review and confirm"/>

          {!!initialDepositAmount && (<CardView noMarginTop>
              <View style={styles.cardHeader}>
                <View style={styles.flex}>
                  <Text Text-16>Initial deposit</Text>
                </View>
                <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                  <IconCoins alt width={24} height={24} color={colors.elements.secondary}/>
                </Squircle>
              </View>
              <LineSeparator noMargin/>
              <CardRowContainer>
                <CardRow title={<RowLabelWithTooltip label="Estimated collection date" onPress={showEstimatedCollectionDateModal}/>} info={estimatedCollectionDate} small smallPadding requesting={fetchingEstimatedDate}/>
                <CardRow title="Account" info={accountName} small smallPadding/>
                <CardRow title="Amount" info={<Text Numbers-14>{formatAmount(initialDepositAmount, 'GBP', 2)}</Text>} small smallPadding/>
              </CardRowContainer>
            </CardView>)}

          {!!onboarding?.collectionFrequency && (<CardView noMarginTop>
              <View style={styles.cardHeader}>
                <View style={styles.flex}>
                  <Text Text-16>Autosave</Text>
                </View>
                <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                  <IconRocket colorKey="brand"/>
                </Squircle>
              </View>
              <LineSeparator noMargin/>
              <CardRowContainer>
                <CardRow title="Saving rules" info={savingRules} small smallPadding/>
                <CardRow title="Collection date" info={collectionDateString} small smallPadding/>
                <CardRow title="Account" info={accountName} small smallPadding/>
                {typeof fees === 'number' && (<CardRow title={<RowLabelWithTooltip label="Autosave fee" onPress={showFeeModal}/>} info={<Text Numbers-14>{formatAmount(fees, 'GBP', 2)}</Text>} small smallPadding/>)}
              </CardRowContainer>
            </CardView>)}
        </Animated.ScrollView>
        <BottomPanel isInTab={!!mountedOnAnotherScreen}>
          <View style={styles.textBtnWrapper}>
            <TextButton small onPress={handleLearnMore} text={<Text TextThin-16 Secondary>
                  <Text TextThin-16 Primary>
                    T&Cs
                  </Text>{' '}
                  apply
                </Text>}/>
          </View>
          <Button brand title="Confirm" onPress={handleConfirm}/>
        </BottomPanel>
      </AppView>);
};
const styleSet = createStyleSheets(() => ({
    cardHeader: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    headerRight: {
        alignItems: 'flex-end',
        paddingRight: rem(16),
        marginBottom: rem(6),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    textBtnWrapper: {
        alignItems: 'center',
        marginBottom: rem(16),
    },
}));
export default PotsOnboardingReviewDepositAndAutosaveInfoScreen;
